import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { stageReserves, type StageReserves } from '../stageLayout';
import { useIsSleek } from '../../theme';

export interface WinnerBands {
  /** The winner's title band, in css px. */
  top: number;
  /** The winner's panel band, in css px. */
  bottom: number;
}

/**
 * The bands the winner chrome reserves — the title above the card, the panel
 * below it — as css px for the scene to keep clear. They are CSS lengths that
 * scale with `svh`, so they resolve only in the DOM: `probes` are two
 * invisible elements carrying them, to render inside the stage. They measure
 * the moment they attach — before the scene can mount, so its card's resting
 * place is laid out from them at boot — and again on resize, which the scene
 * applies once nothing is in flight. Attach-time rather than mount-time
 * because the probes come and go with the stage they sit in: the preview has
 * no stage until its first message arrives.
 *
 * `contained`: a stage in a box of its own brings the bands its host draws
 * there, as lengths of that box, in place of the screen's.
 */
export function useWinnerBands(contained?: StageReserves): { bands: WinnerBands; probes: ReactNode } {
  // Same bands in the winner, the reel and the opener scene (see stageLayout).
  const screenReserves = stageReserves(useIsSleek());
  const reserves = contained ?? screenReserves;
  const nodes = useRef<{ top: HTMLDivElement | null; bottom: HTMLDivElement | null }>({
    top: null,
    bottom: null,
  });
  const [bands, setBands] = useState<WinnerBands>({ top: 0, bottom: 0 });

  const measure = useCallback(() => {
    const top = Math.round(nodes.current.top?.getBoundingClientRect().height ?? 0);
    const bottom = Math.round(nodes.current.bottom?.getBoundingClientRect().height ?? 0);
    setBands((current) =>
      current.top === top && current.bottom === bottom ? current : { top, bottom },
    );
  }, []);

  const attachTop = useCallback(
    (node: HTMLDivElement | null) => {
      nodes.current.top = node;
      if (node) measure();
    },
    [measure],
  );
  const attachBottom = useCallback(
    (node: HTMLDivElement | null) => {
      nodes.current.bottom = node;
      if (node) measure();
    },
    [measure],
  );

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const probes = (
    <>
      <div
        ref={attachTop}
        aria-hidden
        data-band="top"
        className="pointer-events-none invisible absolute left-0 top-0 w-px"
        style={{ height: reserves.top }}
      />
      <div
        ref={attachBottom}
        aria-hidden
        data-band="bottom"
        className="pointer-events-none invisible absolute bottom-0 left-0 w-px"
        style={{ height: reserves.bottom }}
      />
    </>
  );

  return { bands, probes };
}
